import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../../../lib/utils";
import { ArrowDown2, HambergerMenu } from "iconsax-react";
import { sidebarConfig } from "../../../config/sidebarConfig";
import { Button } from "../../../components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/ui/popover";

/* ===============================
   Sidebar Item Component
 ================================= */

const SidebarItem = ({ item, collapsed, pathname, openItem, setOpenItem }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const Icon = item.icon;

  const isActive = item.href && pathname === item.href;
  const hasActiveChild = item.children?.some(
    (child) => pathname === child.href
  );

  const isParentActive = hasActiveChild && !collapsed;
  const isOpen = openItem === item.label;

  /* ===============================
     COLLAPSED MODE (POPOVER)
  ================================= */

  if (item.children && collapsed) {
    return (
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "w-full flex items-center justify-center",
              "px-2 py-2.5 mx-2 rounded-md",
              "transition-colors",
              "hover:bg-muted",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              hasActiveChild && "bg-muted"
            )}
          >
            {Icon && <Icon size={18} />}
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="right"
          align="start"
          sideOffset={8}
          className="
            z-[9999]
            w-56
            p-2
            rounded-xl
            border
            bg-popover
            text-popover-foreground
            shadow-xl
            flex
            flex-col
            gap-1
          "
        >
          {item.children.map((child, idx) => {
            const isChildActive = pathname === child.href;
            const ChildIcon = child.icon;

            return (
              <Link
                key={idx}
                to={child.href}
                onClick={() => setPopoverOpen(false)}
                className={cn(
                  "flex items-center gap-3",
                  "w-full px-3 py-2",
                  "text-sm rounded-md",
                  "transition-colors",
                  "hover:bg-muted",
                  isChildActive
                    ? "bg-primary text-primary-foreground font-medium"
                    : "text-foreground"
                )}
              >
                {ChildIcon && (
                  <ChildIcon
                    size={16}
                    className="shrink-0 text-muted-foreground"
                  />
                )}
                <span>{child.label}</span>
              </Link>
            );
          })}
        </PopoverContent>
      </Popover>
    );
  }

  /* ===============================
     EXPANDED MODE (DROPDOWN)
  ================================= */

  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpenItem(isOpen ? null : item.label)}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 mx-2 rounded-md transition-colors",
            "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            isParentActive && "bg-muted font-medium"
          )}
        >
          {Icon && <Icon size={18} />}
          <span className="text-sm flex-1 text-left">{item.label}</span>
          <ArrowDown2
            size={14}
            className={cn(
              "transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </button>

        <div
          className={cn(
            "overflow-hidden transition-all duration-200 ease-in-out",
            isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="py-1">
            {item.children.map((child, idx) => {
              const isChildActive = pathname === child.href;

              return (
                <Link
                  key={idx}
                  to={child.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 mx-2 rounded-md text-sm transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isChildActive
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-foreground hover:bg-muted",
                    "pl-11"
                  )}
                >
                  {child.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  /* ===============================
     SINGLE ITEM
  ================================= */

  return (
    <Link
      to={item.href}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 mx-2 rounded-md transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isActive
          ? "bg-primary text-primary-foreground font-medium"
          : "text-foreground hover:bg-muted",
        collapsed && "justify-center px-2"
      )}
      title={collapsed ? item.label : undefined}
    >
      {Icon && <Icon size={18} />}
      {!collapsed && <span className="text-sm">{item.label}</span>}
    </Link>
  );
};

/* ===============================
   Sidebar Component
 ================================= */

const Sidebar = ({ open, onToggle, onCollapsedChange }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  const { pathname } = useLocation();

  const handleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    if (onCollapsedChange) onCollapsedChange(newCollapsed);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        className={cn(
          "fixed top-0 left-0 h-screen border-r bg-background flex flex-col",
          "transition-all duration-300 ease-in-out",
          "z-40",
          collapsed ? "w-16" : "w-64",
          open ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b h-16 flex-shrink-0">
          {!collapsed && (
            <img
              src="/OptiHomecare.png"
              alt="Logo"
              className="h-9 w-auto object-contain"
            />
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={handleCollapse}
            className={cn(collapsed && "mx-auto")}
          >
            <HambergerMenu size={18} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2">
          {sidebarConfig.map((section, idx) => (
            <div key={idx} className="mb-2">
              {section.items.map((item, itemIdx) => (
                <SidebarItem
                  key={itemIdx}
                  item={item}
                  collapsed={collapsed}
                  pathname={pathname}
                  openItem={openItem}
                  setOpenItem={setOpenItem}
                />
              ))}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
